<template>
  <div class="legal-page" ref="pageContainer">
    <!-- Monogram Background Layer -->
    <div class="monogram-wrapper">
      <div class="monogram-background"></div>
    </div>

    <!-- Header Hero Section -->
    <section class="legal-hero">
      <div class="container">
        <div class="hero-content">
          <span class="hero-badge">{{ t('faq.badge') || '// CUSTOMER CARE' }}</span>
          <h1 class="hero-title">{{ t('faq.title') || 'ASSISTENZA' }}</h1>
          <div class="hero-divider"></div>
          <p class="last-update">{{ currentTabLabel }}</p>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="legal-body">
      <div class="container">
        <div class="legal-layout">
          <!-- Sidebar Navigation -->
          <aside class="legal-sidebar desktop-only">
            <div class="sidebar-sticky">
              <nav class="side-nav">
                <a 
                  v-for="(tab, index) in tabs" 
                  :key="tab.id"
                  href="javascript:void(0)"
                  class="nav-anchor"
                  :class="{ active: activeTab === tab.id }"
                  @click="setTab(tab.id)"
                >
                  <span class="nav-num">{{ (index + 1).toString().padStart(2, '0') }}</span>
                  <span class="nav-text">{{ tab.label }}</span>
                </a>
              </nav>
            </div>
          </aside>

          <!-- Main Legal Content -->
          <div class="legal-main">
            <!-- Mobile Navigation (Tabs) -->
            <div class="mobile-tabs-container mobile-only">
              <div class="mobile-tabs">
                <button 
                  v-for="tab in tabs" 
                  :key="tab.id"
                  class="mobile-tab-btn"
                  :class="{ active: activeTab === tab.id }"
                  @click="setTab(tab.id)"
                >
                  {{ tab.label }}
                </button>
              </div>
            </div>

            <div class="legal-section-card">
              <div class="card-inner">
                <div class="section-header">
                  <span class="section-index">// FAQ</span>
                  <h2>{{ currentTabLabel }}</h2>
                </div>
                
                <div class="section-content">
                  <div class="faq-list">
                    <div 
                      v-for="faq in filteredFaqs" 
                      :key="faq.id" 
                      class="faq-item"
                      :class="{ 'faq-item-active': activeFAQ === faq.id }"
                    >
                      <button class="faq-question" @click="toggleFAQ(faq.id)">
                        <span>{{ faq.q }}</span>
                        <span class="faq-toggle">{{ activeFAQ === faq.id ? '−' : '+' }}</span>
                      </button>
                      <div v-if="activeFAQ === faq.id" class="faq-answer-container">
                        <div class="faq-answer" v-html="faq.a"></div>
                      </div>
                    </div>

                    <div v-if="filteredFaqs.length === 0" class="no-results">
                      {{ t('faq.contact_info.no_results') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contact Support Section -->
            <div class="legal-section-card support-section">
              <div class="card-inner support-card">
                <div class="support-content">
                  <div class="support-header">
                    <span class="support-badge">// {{ t('faq.contact_info.email_title') }}</span>
                    <h3>{{ t('faq.contact_info.not_found') }}</h3>
                  </div>
                  
                  <div class="support-action">
                    <a :href="`mailto:${supportEmail}`" class="support-link">{{ supportEmail }}</a>
                    <div class="support-footer">
                      <span class="support-icon">✉</span>
                      <p>{{ t('faq.contact_info.email_subtitle') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const configStore = useConfigStore()
const { t } = useLanguage()
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL
const pageContainer = ref(null)

const activeTab = ref('all')
const activeFAQ = ref(null)

const tabs = computed(() => [
  { id: 'all', label: t('faq.tabs.all') },
  { id: 'spedizioni', label: t('faq.tabs.ship') },
  { id: 'resi', label: t('faq.tabs.ret') },
  { id: 'pagamenti', label: t('faq.tabs.pay') }
])

const faqData = computed(() => [
  { 
    id: 1, 
    cat: 'spedizioni', 
    q: t('faq.ship.q1'), 
    a: t('faq.ship.a1') 
  },
  { 
    id: 2, 
    cat: 'spedizioni', 
    q: t('faq.ship.q2'), 
    a: t('faq.ship.a2', { amount: configStore.freeShippingThreshold.toFixed(2).replace('.', ',') }) 
  },
  { 
    id: 3, 
    cat: 'spedizioni', 
    q: t('faq.ship.q3'), 
    a: t('faq.ship.a3') 
  },
  { 
    id: 4, 
    cat: 'resi', 
    q: t('faq.ret.q1'), 
    a: t('faq.ret.a1') 
  },
  { 
    id: 5, 
    cat: 'resi', 
    q: t('faq.ret.q2'), 
    a: t('faq.ret.a2') 
  },
  { 
    id: 6, 
    cat: 'resi', 
    q: t('faq.ret.q3'), 
    a: t('faq.ret.a3') 
  },
  { 
    id: 7, 
    cat: 'pagamenti', 
    q: t('faq.pay.q1'), 
    a: t('faq.pay.a1') 
  }
])

const currentTabLabel = computed(() => {
  const tab = tabs.value.find(t => t.id === activeTab.value)
  return tab ? tab.label : t('faq.tabs.all')
})

const filteredFaqs = computed(() => {
  if (activeTab.value === 'all') return faqData.value
  return faqData.value.filter(item => item.cat === activeTab.value)
})

const setTab = (id) => {
  router.push({ query: { tab: id } })
}

const toggleFAQ = (id) => {
  activeFAQ.value = activeFAQ.value === id ? null : id
}

watchEffect(() => {
  const tab = route.query.tab
  const path = route.path

  if (tab) {
    activeTab.value = tab.toString()
  } else if (path.includes('spedizioni')) {
    activeTab.value = 'spedizioni'
  } else if (path.includes('resi')) {
    activeTab.value = 'resi'
  } else if (path.includes('pagamenti')) {
    activeTab.value = 'pagamenti'
  } else if (path.includes('faq')) {
    activeTab.value = 'all'
  } else {
    activeTab.value = 'all'
  }

  if (activeTab.value !== 'all' && filteredFaqs.value.length > 0) {
    activeFAQ.value = filteredFaqs.value[0].id
  } else {
    activeFAQ.value = null
  }

  if (pageContainer.value) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }
})

onMounted(() => {
  if (activeTab.value !== 'all' && filteredFaqs.value.length > 0) {
    activeFAQ.value = filteredFaqs.value[0].id
  }
})
</script>

<style scoped>
.legal-page {
  background-color: #ffffff;
  color: #000;
  min-height: 100vh;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow-x: hidden;
}

.monogram-wrapper {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.monogram-background {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: url("@/assets/images/logo.png");
  background-size: 120px auto;
  background-repeat: repeat;
  opacity: 0.22;
  transform: rotate(-15deg);
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 25px;
  position: relative;
  z-index: 1;
}

.legal-hero {
  padding: 40px 0 25px;
  background-color: #000;
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: block;
  font-size: 9px;
  font-weight: 800;
  color: var(--bright-teal);
  letter-spacing: 3px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.hero-title {
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  margin-bottom: 12px;
  line-height: 1.1;
  color: #fff;
}

.hero-divider {
  width: 30px;
  height: 2px;
  background-color: var(--bright-teal);
  margin: 0 auto 12px;
}

.legal-body {
  padding: 60px 0 100px;
  position: relative;
  z-index: 1;
}

.legal-layout {
  display: flex;
  gap: 50px;
}

@media (max-width: 992px) {
  .legal-layout {
    flex-direction: column;
    gap: 25px;
  }
}

.legal-sidebar {
  flex: 0 0 240px;
  position: relative;
  z-index: 2;
}

.sidebar-sticky {
  position: sticky;
  top: 110px;
  background-color: #fff;
  padding: 25px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nav-anchor {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 15px;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  cursor: pointer;
}

.nav-num {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 800;
  color: var(--dark-teal);
  margin-top: 2px;
}

.nav-text {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-anchor:hover .nav-text,
.nav-anchor.active .nav-text {
  color: var(--dark-teal);
}

.nav-anchor:hover,
.nav-anchor.active {
  border-left-color: var(--dark-teal);
  background: rgba(0, 0, 0, 0.02);
}

.legal-main {
  flex: 1;
}

.legal-section-card {
  margin-bottom: 30px;
}

.card-inner {
  background-color: #000;
  padding: 40px;
  border-radius: 4px;
  border-left: 4px solid var(--bright-teal);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.section-header {
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
}

.section-index {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 800;
  color: var(--bright-teal);
  margin-bottom: 5px;
  letter-spacing: 2px;
}

.section-header h2 {
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  color: #fff;
}

/* FAQ Styles Override */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.faq-question {
  width: 100%;
  padding: 20px 0;
  background: transparent;
  border: none;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faq-question:hover {
  color: var(--bright-teal);
}

.faq-item-active .faq-question {
  color: var(--bright-teal);
}

.faq-toggle {
  font-size: 20px;
  color: var(--bright-teal);
  font-family: monospace;
}

.faq-answer-container {
  padding-bottom: 20px;
}

.faq-answer {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  font-size: 14px;
  white-space: pre-wrap;
}

.no-results {
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

/* Support Section */
.support-section {
  margin-top: 40px;
}

.support-card {
  background-color: #000;
  border-left-color: var(--bright-teal);
  padding: 50px !important;
  text-align: center;
}

.support-header {
  margin-bottom: 30px;
}

.support-badge {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 800;
  color: var(--bright-teal);
  margin-bottom: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.support-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.support-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.support-link {
  font-size: clamp(18px, 4vw, 24px);
  font-weight: 900;
  color: #fff;
  text-decoration: none;
  transition: all 0.3s ease;
  letter-spacing: -0.5px;
}

.support-link:hover {
  color: var(--bright-teal);
  transform: translateY(-2px);
}

.support-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0.5;
}

.support-icon {
  font-size: 16px;
  color: var(--bright-teal);
}

.support-footer p {
  font-size: 11px;
  color: #fff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Mobile Tabs Navigation */
.mobile-tabs-container {
  margin-bottom: 20px;
}

.mobile-tabs {
  display: flex;
  overflow-x: auto;
  gap: 10px;
  padding-bottom: 10px;
  scrollbar-width: none;
}

.mobile-tabs::-webkit-scrollbar {
  display: none;
}

.mobile-tab-btn {
  white-space: nowrap;
  padding: 10px 20px;
  background: #000;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.mobile-tab-btn.active {
  background: var(--bright-teal);
  color: #000;
  border-color: var(--bright-teal);
}

.mobile-only {
  display: none;
}

.legal-sidebar {
  flex: 0 0 240px;
  position: relative;
  z-index: 2;
}

@media (max-width: 992px) {
  .legal-sidebar {
    flex: 0 0 auto;
    width: 100%;
  }
  .sidebar-sticky {
    position: static;
  }
}

@media (max-width: 768px) {
  .legal-sidebar {
    display: none !important;
  }
  .mobile-only {
    display: block;
  }
  .card-inner {
    padding: 22px 18px;
  }
  .legal-body {
    padding: 30px 0 60px;
  }
  .legal-hero {
    padding: 25px 0 18px;
  }
  .legal-layout {
    flex-direction: column;
    gap: 20px;
  }
  .container {
    padding: 0 16px;
  }
  .faq-question {
    font-size: 14px;
    padding: 14px 0;
    line-height: 1.4;
  }
  .faq-toggle {
    font-size: 18px;
  }
  .faq-answer {
    font-size: 13px;
  }
  .section-header h2 {
    font-size: 16px;
  }
  .support-card {
    padding: 30px 20px !important;
  }
  .support-header h3 {
    font-size: 14px;
  }
  .support-link {
    font-size: 18px;
    word-break: break-all;
  }
  .monogram-background {
    background-size: 60px auto;
  }
  .no-results {
    padding: 25px 15px;
  }
}

@media (max-width: 480px) {
  .legal-body {
    padding: 20px 0 50px;
  }
  .card-inner {
    padding: 18px 14px;
  }
  .mobile-tab-btn {
    padding: 8px 14px;
    font-size: 10px;
  }
  .hero-title {
    font-size: 20px;
  }
  .container {
    padding: 0 12px;
  }
}
</style>
